import { useState } from 'react'
import styled from 'styled-components'

import ButtonLink from 'components/ButtonLink'
import Modal from 'components/Modal'
import Spinner from 'components/Spinner'
import type { RdnCalculationDetails, RdnPricingMethod } from 'types/match'

type Props = {
  loadDetails: () => Promise<RdnCalculationDetails | null>
}

export default function RdnPricingDetailsAction({ loadDetails }: Props) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [details, setDetails] = useState<RdnCalculationDetails | null>(null)
  const [error, setError] = useState(false)
  const averagePrice = details ? calculateAveragePrice(details) : 0

  const showDetails = async () => {
    setOpen(true)
    setLoading(true)
    setError(false)
    setDetails(null)
    try {
      const loaded = await loadDetails()
      if (loaded) setDetails(loaded)
      else setError(true)
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <ButtonLink onClick={showDetails}>Pokaż ceny RDN</ButtonLink>
      <StyledModal isOpen={open} onClose={() => setOpen(false)}>
        <h1>Szczegóły cen RDN</h1>
        {loading && <Loading><Spinner size={28} /> Ładowanie cen RDN…</Loading>}
        {error && <Error>Nie udało się pobrać szczegółów cen RDN. Spróbuj ponownie.</Error>}
        {details && !loading && (
          <>
            <Metadata>
              <strong>{methodName(details.method)}</strong>
              {details.pricing_cutoff_date && <span>Data graniczna: {details.pricing_cutoff_date}</span>}
              <span>Korekta RDN: {details.rdn_percentage_adjustment}%</span>
              <span>{details.displayed_price_formula}</span>
              <span>{details.excluded_costs}</span>
            </Metadata>
            <MatrixWrapper>
              <table>
                <thead><tr><th>Data</th>{Array.from({ length: 24 }, (_, hour) => <th key={hour}>{hour}</th>)}</tr></thead>
                <tbody>{details.days.map(day => <tr
                  className={isWeekend(day.date, details) ? 'weekend' : undefined}
                  key={day.calculation_day_of_year}
                >
                  <th>{day.date}</th>
                  {day.hours.map(hour => <td
                    className={priceClassName(hour.price, averagePrice)}
                    key={hour.hour_of_day}
                  >
                    {hour.price.toFixed(2)}
                  </td>)}
                </tr>)}</tbody>
              </table>
            </MatrixWrapper>
          </>
        )}
      </StyledModal>
    </>
  )
}

function methodName(method: RdnPricingMethod): string {
  if (method.kind === 'ANNUAL_HOURLY_PROFILE') {
    return `Dobowy profil godzinowy: śr. z ${method.observations} dni, ${
      method.skip_weekends ? 'weekendy pominięte' : 'weekendy uwzględnione'}`
  }
  if (method.kind === 'CALENDAR_MOVING_AVERAGE') {
    return `Średnia krocząca kalendarzowa: okno ${method.window_days} dni, historia ${
      method.history_years} lat`
  }
  return `Rok historyczny ${method.year}`
}

function isWeekend(date: string, details: RdnCalculationDetails): boolean {
  const year = details.method.kind === 'HISTORICAL_YEAR'
    ? details.method.year
    : Number(details.pricing_cutoff_date?.slice(0, 4))
  if (!year) return false

  const [day, month] = date.split('.').map(Number)
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay()
  return weekday === 0 || weekday === 6
}

function calculateAveragePrice(details: RdnCalculationDetails): number {
  let total = 0
  let count = 0
  details.days.forEach(day => day.hours.forEach(hour => {
    total += hour.price
    count += 1
  }))
  return count ? total / count : 0
}

function priceClassName(price: number, averagePrice: number): string | undefined {
  if (price < 0) return 'negative'
  if (price > averagePrice * 2) return 'high'
  return undefined
}

const StyledModal = styled(Modal)`
  width: min(1200px, calc(100vw - 32px));
`

const Loading = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const Error = styled.p`
  color: var(--red);
`

const Metadata = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 20px;
  align-self: stretch;
`

const MatrixWrapper = styled.div`
  width: 100%;
  overflow: auto;

  table { border-collapse: collapse; font: var(--400-12); }
  th, td { border: 1px solid var(--light-gray); white-space: nowrap; }
  th { padding: 4px 8px; }
  td { padding: 2px 4px; font-size: 10px; }
  th { position: sticky; top: 0; background: var(--white); }
  tr.weekend th, tr.weekend td { background: #eef0f5; }
  td.negative { color: var(--blue); }
  td.high { color: var(--red); }
`
